import React, { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Trash2, Upload, Check } from 'lucide-react';

const SignatureCapture = ({ value, onChange, error }) => {
  const sigCanvas = useRef(null);
  const fileInputRef = useRef(null);
  const [signatureType, setSignatureType] = useState('draw'); // 'draw' or 'upload'
  const [uploadedImage, setUploadedImage] = useState(null);
  
  // Clear the signature pad
  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
    onChange(null);
  };
  
  // Save the signature as a PNG data URL
  const saveSignature = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      // Get the signature as a PNG data URL
      const dataURL = sigCanvas.current.toDataURL('image/png');
      console.log("Saving signature:", dataURL.substring(0, 50) + "...");
      onChange(dataURL);
    }
  };
  
  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.match('image.*')) {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        setUploadedImage(event.target.result);
        onChange(event.target.result);
      };
      
      reader.readAsDataURL(file);
    }
  };
  
  // Trigger file input click
  const triggerFileUpload = () => {
    fileInputRef.current.click();
  };
  
  return (
    <div className="space-y-4">
      {/* Signature Type Selection */}
      <div className="flex space-x-4 mb-4">
        <button
          type="button"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            signatureType === 'draw'
              ? 'bg-purple-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
          onClick={() => setSignatureType('draw')}
        >
          Draw Signature
        </button>
        
        <button
          type="button"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            signatureType === 'upload'
              ? 'bg-purple-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
          onClick={() => setSignatureType('upload')}
        >
          Upload Signature
        </button>
      </div>
      
      {/* Draw Signature */}
      {signatureType === 'draw' && (
        <div className="space-y-3">
          <div className="bg-white rounded-lg overflow-hidden" style={{ width: '500px', height: '200px' }}>
            <SignatureCanvas
              ref={sigCanvas}
              penColor="black"
              canvasProps={{
                width: 500,
                height: 200,
                style: {
                  width: '500px',
                  height: '200px'
                }
              }}
              backgroundColor="white"
            />
          </div>
          
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={clearSignature}
              className="px-3 py-1.5 bg-gray-700 text-gray-300 rounded-lg text-sm flex items-center hover:bg-gray-600 transition-colors"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Clear
            </button>
            
            <button
              type="button"
              onClick={saveSignature}
              className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm flex items-center hover:bg-green-700 transition-colors"
            >
              <Check className="h-4 w-4 mr-1" />
              Save Signature
            </button>
          </div>
        </div>
      )}
      
      {/* Upload Signature */}
      {signatureType === 'upload' && (
        <div className="space-y-3">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept="image/*"
            className="hidden"
          />
          
          {uploadedImage ? (
            <div className="space-y-3">
              <div className="bg-white p-4 rounded-lg">
                <img
                  src={uploadedImage}
                  alt="Uploaded Signature"
                  className="max-h-48 mx-auto"
                />
              </div>
              
              <button
                type="button"
                onClick={triggerFileUpload}
                className="px-3 py-1.5 bg-gray-700 text-gray-300 rounded-lg text-sm flex items-center hover:bg-gray-600 transition-colors"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Change Image
              </button>
            </div>
          ) : (
            <div
              className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center cursor-pointer hover:border-purple-500 transition-colors"
              onClick={triggerFileUpload}
            >
              <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
              <p className="text-gray-300">Click to upload your signature image</p>
              <p className="text-xs text-gray-500 mt-1">PNG, JPG, or GIF files accepted</p>
            </div>
          )}
        </div>
      )}
      
      {/* Display saved signature */}
      {value && (
        <div className="mt-4">
          <p className="text-sm text-green-400 mb-2 flex items-center">
            <Check className="h-4 w-4 mr-1" />
            Signature saved successfully
          </p>
        </div>
      )}
      
      {/* Error message */}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};

export default SignatureCapture;